/***
 *
 *   PROJECT DETAIL
 *   Phase 8 — stacked cards matching project-detail/* (General, Team,
 *   Contacts, Categories, Danger). Privacy stay in ProjectForm.
 *
 **********/

import { useEffect, useContext } from 'react';
import {
    Animate,
    SettingsWrapper,
    useProjectContext,
    useBreadcrumbsContext,
    Link,
    Button,
    ViewContext,
    Card,
    ProjectMark,
} from 'components/lib';
import { useParams, useLocation } from 'react-router-dom';
import { ProjectForm } from 'components/form/template/project';
import { useUsers, useProjectUsers } from '../../../components/hooks/users';
import ProjectUsers from './users';
import ProjectContacts from './contacts';
import ProjectCategories from './categories';
import ProjectAutoDelete from './autoDelete';
import ProjectPortal from './portal';
import { useTranslate } from 'app/translations';
import Style from './edit.tailwind';

export function EditProject() {
    const { id } = useParams();
    const { state, hash } = useLocation();
    const { projects, selectAllProjects, selectedProject } = useProjectContext();
    const { pushParams } = useBreadcrumbsContext();
    const { _t } = useTranslate();
    const projectUsers = useProjectUsers(id);
    const users = useUsers(id);
    const viewContext = useContext(ViewContext);
    const project = projects?.find((p) => p.id === id);
    const isCurrentProject = selectedProject?.id === id;

    useEffect(() => {
        if (project) {
            pushParams({
                id: project.name,
            });
        }
    }, [project]);

    useEffect(() => {
        if (hash !== '#ticket-categories') return;
        const run = () =>
            document
                .getElementById('ticket-categories')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const frame = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(run);
        });
        return () => window.cancelAnimationFrame(frame);
    }, [hash, id, projectUsers.isLoading, users.isLoading]);

    function deleteProject() {
        viewContext.modal.show(
            {
                title: _t('project.delete.label'),
                form: {},
                text: _t('project.delete.confirm'),
                buttonText: _t('project.delete.label'),
                url: `/api/project/${id}`,
                destructive: true,
                method: 'DELETE',
            },
            (res) => {
                if (res.status === 200) {
                    if (isCurrentProject) selectAllProjects();
                    window.location.href = '/app/settings/projects';
                }
            },
        );
    }

    return (
        <SettingsWrapper>
            <Animate>
                <div className={Style.stack}>
                    <div className={Style.header}>
                        <div className={Style.identity}>
                            <ProjectMark
                                name={project?.name || state?.title}
                                filePath={project?.file_path}
                                className={Style.mark}
                                fallbackClassName={Style.markFallback}
                            />
                            <div className="min-w-0">
                                <h1 className={Style.title}>
                                    {project?.name || state?.title || '—'}
                                </h1>
                                <p className={Style.description}>{_t('project.configure')}</p>
                            </div>
                        </div>
                        {project?.name && (
                            <Link
                                className={Style.viewLink}
                                url={`/project/${project.name}`}
                                text={_t('project.viewPage')}
                            />
                        )}
                    </div>

                    <ProjectForm
                        method="PATCH"
                        url={`/api/project/${id}`}
                        id={id}
                        sectionTitle={_t('settings.sections.general')}
                        sectionDescription={_t('project.update.information')}
                    />

                    <ProjectAutoDelete />

                    <ProjectPortal />

                    <ProjectCategories />

                    {!projectUsers.isLoading && !users.isLoading && (
                        <>
                            <ProjectUsers
                                users={users}
                                projectUsers={projectUsers}
                                project={state?.title}
                            />
                            <ProjectContacts
                                users={users}
                                projectUsers={projectUsers}
                                project={state?.title}
                            />
                        </>
                    )}

                    <Card
                        title={_t('project.delete.label')}
                        description={_t('project.delete.confirm')}
                        className={Style.dangerCard}>
                        <Button
                            variant="danger"
                            size="sm"
                            text={_t('project.delete.label')}
                            action={deleteProject}
                        />
                    </Card>
                </div>
            </Animate>
        </SettingsWrapper>
    );
}
